import {usePlantStatus} from '@domain/kpis'
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {STOPPAGE_ANALYSIS_SUB_PATH} from '@features/stoppage-analysis'
import {DashboardLink} from '@hconnect/common/components/downtime'
import {DataContentWrapper} from '@hconnect/common/containers'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {useUpmPlant, useUpmPlantTimezone} from '@hconnect/upm'
import {Theme, useMediaQuery} from '@mui/material'
import {RcfaStatusTableContainer} from '@widgets/downtime-analytics'
import {MaterialQualityParametersContainer} from '@widgets/kpis'
import {PomScoreProgressContainer} from '@widgets/pom-completion'
import {useTranslation} from 'react-i18next'
import {generatePath, useNavigate} from 'react-router'

import {PageHeader, PlantStatusFilters} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {
  ExpertSystemContainer,
  PlantActualVsPlanned,
  PlantRunningTimesCard,
  PlantEntryPointDailyKpiSummary
} from '../containers'
import {
  usePlantStatusDateRange,
  usePlantStatusPageParams,
  useTrackPlantStatusAnalyticsEvent
} from '../hooks'
import {PlantStatusSummaryLayout, PlantStatusTrendLayout} from '../layouts'
import {PlantStatusAnalyticsEventName} from '../types'

const SummaryViewQuickSelectionIds = ['today', 'yesterday', 'last7days']

type Props = {
  plantId: string
  easyPomLinkPath?: string
  stoppageListHref: string
  shiftSummaryHref: string
  backButtonProps?: {
    targetName: string
    target: string
  }
  // true when hosted on the legacy blue surface; false (default) on the Suite grey surface
  inverted?: boolean
}

export const PlantStatusPage = ({
  plantId,
  easyPomLinkPath,
  stoppageListHref,
  shiftSummaryHref,
  backButtonProps,
  inverted = false
}: Props) => {
  const {upmId: upmPlantId} = useUpmPlant({plantId})
  const navigate = useNavigate()
  const trackAnalyticsEvent = useTrackPlantStatusAnalyticsEvent(plantId)
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const {timeRangeParam, setTimeRangeParam} = usePlantStatusPageParams()
  const {from, to, quickSelectionId} = usePlantStatusDateRange({timezone})
  const dateRangeIso = {from: from.toISOString(), to: to.toISOString()}
  const useSummaryLayout =
    quickSelectionId && SummaryViewQuickSelectionIds.includes(quickSelectionId)
  const {data, isLoading, isError, refetch, isRefetching} = usePlantStatus(plantId, {
    startDate: dateRangeIso.from,
    endDate: dateRangeIso.to
  })

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const onStoppagePreviewOpen = (stoppageId: string) =>
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserViewsStoppage, {
      stoppageId
    })
  const onStoppageCreate = (stoppageId: string) =>
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserCreatesStoppage, {
      stoppageId
    })
  const onStoppageEdit = (stoppageId: string) =>
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.userEditsStoppage, {
      stoppageId
    })
  const onStoppageDelete = (stoppageId: string) =>
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.userDeletesStoppage, {
      stoppageId
    })
  const onStoppageSplit = (stoppageId: string) =>
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.userSplitsStoppage, {
      stoppageId
    })

  const redirectToStoppageAnalysis = (equipmentId: string) => {
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.userGoesToStoppageAnalysisPage, {
      equipmentId
    })

    void navigate(
      generatePath(STOPPAGE_ANALYSIS_SUB_PATH, {
        equipmentId
      })
    )
  }

  const handleRcfaStatusCardClick = () => {
    trackAnalyticsEvent(PlantStatusAnalyticsEventName.userClicksOnRCFAStatus, {})
  }

  return (
    <PageContainer>
      <DataContentWrapper
        showLoader={isLoading}
        data={data}
        retryFunction={() => void refetch()}
        isError={isError}
        isRefetching={isRefetching}
      >
        <PageHeader
          countryCode={data?.plant?.countryCode}
          title={data?.plant.name || ''}
          actionButtons={
            <DashboardLink
              title={t('label.goToDowntimeDashboard')}
              plantId={plantId}
              variant="outlined"
              styleVariant={inverted ? 'onBlue' : 'onWhite'}
              fullWidth={isMobile}
              onClick={() =>
                trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserGoesToDowntimeDashboard, {})
              }
            />
          }
          filterComponent={
            <PlantStatusFilters
              timeRangeParam={timeRangeParam}
              onTimeRangeParamChange={(newTimeRange) => {
                setTimeRangeParam(newTimeRange)
                trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserAdjustsDateRangeFilter, {})
              }}
              timezone={timezone}
              inverted={inverted}
            />
          }
          backButtonProps={backButtonProps}
          onGoBackClick={() =>
            trackAnalyticsEvent(PlantStatusAnalyticsEventName.UserGoesBackToCountryOverview, {})
          }
        />
        {useSummaryLayout ? (
          <PlantStatusSummaryLayout
            generalSlot={
              <PlantEntryPointDailyKpiSummary
                plantId={plantId}
                timezone={timezone}
                shiftSummaryHref={shiftSummaryHref}
              />
            }
            qualityKpisSlot={
              <MaterialQualityParametersContainer
                plantId={plantId}
                cardBoxSx={{
                  width: '100%',
                  height: '450px',
                  marginTop: isError || isRefetching || isLoading ? 'initial' : 'unset'
                }}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                placeholderWrapperSx={{height: '100%'}}
                timezone={timezone}
                isVerticalLayout={true}
              />
            }
            runningTimesSlot={
              <PlantRunningTimesCard
                plantId={plantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                timezone={timezone}
                onEquipmentClick={redirectToStoppageAnalysis}
                onStoppagePreviewOpen={onStoppagePreviewOpen}
                onStoppageCreate={onStoppageCreate}
                onStoppageEdit={onStoppageEdit}
                onStoppageDelete={onStoppageDelete}
                onStoppageSplit={onStoppageSplit}
              />
            }
            expertSystemSlot={
              <ExpertSystemContainer
                upmPlantId={upmPlantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
              />
            }
            scoreProgressSlot={
              <PomScoreProgressContainer
                plantId={upmPlantId}
                easyPomHref={easyPomLinkPath}
                onClick={() =>
                  trackAnalyticsEvent(PlantStatusAnalyticsEventName.userClicksOnEasyPomOverview, {})
                }
              />
            }
            rcfaSlot={
              <RcfaStatusTableContainer
                plantId={plantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                onCardClick={handleRcfaStatusCardClick}
                stoppageListHref={stoppageListHref}
              />
            }
          />
        ) : (
          <PlantStatusTrendLayout
            janusKpisSlot={
              <PlantActualVsPlanned
                plantId={plantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                quickSelectionId={quickSelectionId}
              />
            }
            runningTimesSlot={
              <PlantRunningTimesCard
                plantId={plantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                timezone={timezone}
                onEquipmentClick={redirectToStoppageAnalysis}
                onStoppagePreviewOpen={onStoppagePreviewOpen}
                onStoppageCreate={onStoppageCreate}
                onStoppageEdit={onStoppageEdit}
                onStoppageDelete={onStoppageDelete}
                onStoppageSplit={onStoppageSplit}
              />
            }
            expertSystemSlot={
              <ExpertSystemContainer
                upmPlantId={upmPlantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
              />
            }
            scoreProgressSlot={
              <PomScoreProgressContainer
                plantId={upmPlantId}
                easyPomHref={easyPomLinkPath}
                onClick={() =>
                  trackAnalyticsEvent(PlantStatusAnalyticsEventName.userClicksOnEasyPomOverview, {})
                }
              />
            }
            rcfaSlot={
              <RcfaStatusTableContainer
                plantId={plantId}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                onCardClick={handleRcfaStatusCardClick}
                stoppageListHref={stoppageListHref}
              />
            }
            qualityKpisSlot={
              <MaterialQualityParametersContainer
                plantId={plantId}
                cardBoxSx={{
                  width: '100%',
                  height: '450px'
                }}
                from={dateRangeIso.from}
                to={dateRangeIso.to}
                placeholderWrapperSx={{height: '100%'}}
                timezone={timezone}
                isVerticalLayout={true}
              />
            }
          />
        )}
      </DataContentWrapper>
    </PageContainer>
  )
}
